import React, { memo, useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import cls from "classnames";
import { useCtx } from "@al/next/al-ex";
import { IRequestConfig } from "@al/aku-lc-core";

import { ChannelType, ShareInfo } from "~/components/basic/Share/types";
import __get__short__link from "~/components/basic/Share/_share/helpers/__get__short__link";

import Urls from "../../redux/urls";

import styles from "./shareCard.scss";

const growthPublishInviteCodeCacheKey = "growth_publish_invite_code_cache";

type Props = {
  backImg: string;
  fetcher: (config: IRequestConfig) => Promise<any>;
  getShareInfo?: (channelId: ChannelType) => ShareInfo | Promise<ShareInfo> | undefined;
};

const shareCard: React.FC<Props> = (props) => {
  const [shareInfo, setShareInfo] = useState<ShareInfo>();
  const [remoteUrl, setRemoteUrl] = useState("");
  const ctx = useCtx();
  const cavEl = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    async function getShareInfo(): Promise<void> {
      const info = await props.getShareInfo?.(2);
      if (!info) return;
      const shareContentUrl = (await __get__short__link(info.shareContentUrl)) || info.shareContentUrl;
      setShareInfo({ ...info, shareContentUrl });
    }
    getShareInfo();
  }, []);

  useEffect(() => {
    const loadImg = (src: string): Promise<HTMLImageElement> => {
      return new Promise((resolve) => {
        const img = new Image();
        img.setAttribute("crossorigin", "anonymous");
        img.onload = async (): Promise<void> => {
          resolve(img);
        };
        img.src = src;
      });
    };

    // 邀请码
    const loadDistributionCode = (): Promise<any> | undefined => {
      const { activityId, activityRuleId } = ctx.router.query;
      if (!activityId || !activityRuleId) return;
      return props.fetcher({
        url: Urls.getShareCode,
        data: {
          // 这里故意写的空字符，这样才能和页面请求中的 body 保持一致，请求时这些空字符参数会自动补全的
          activityId: "",
          activityRuleId: "",
        },
      });
    };

    const putImage = async (file: Blob): Promise<string> => {
      return new Promise((resolve, reject) => {
        props
          .fetcher({
            url: Urls.getUploadUrl,
            data: {
              busiType: 7,
              cnt: 1,
              contentType: "image/jpg",
              fileSuffix: "jpg",
            },
          })
          .then((data) => {
            if (data?.[0]?.url) {
              fetch(data[0].url, {
                method: "put",
                body: file,
              })
                .then(() => {
                  resolve(data[0].cdnUrl);
                })
                .catch(reject);
            }
          })
          .catch(reject);
      });
    };

    async function draw(): Promise<void> {
      if (!shareInfo?.shareContentUrl || !cavEl.current) return;
      const ratio = window.devicePixelRatio; // 设备的devicePixelRadio
      const result = await QRCode.toDataURL(shareInfo.shareContentUrl, { width: 50 });
      const ctx = cavEl.current.getContext("2d");
      if (!ctx) return;
      // 将Canvas画布放大scale倍，然后放在小的屏幕里，解决模糊问题
      cavEl.current.width = ratio * 150;
      cavEl.current.height = ratio * 150;
      ctx.scale(ratio, ratio);

      // 先清空画布
      ctx.clearRect(0, 0, 150, 150);

      // 获取邀请码
      const loadCode = await loadDistributionCode();
      const code = loadCode?.code;

      // 先读取本地 localStorage, 如果有就直接绘制并且不上传，否则走原有绘制逻辑.
      const cacheImg = localStorage.getItem(`${growthPublishInviteCodeCacheKey}:${code}`);
      if (cacheImg) {
        const img = await loadImg(cacheImg);
        ctx?.drawImage(img, 0, 0, 150, 150);
        setRemoteUrl(cacheImg);
        return;
      }

      // 绘制背景图
      const img = await loadImg(props.backImg);
      ctx?.drawImage(img, 0, 0, 150, 150);
      // ctx.fillStyle = "orange";
      // ctx.fillRect(0, 0, (200), (300));

      // 绘制二维码
      const qrCodeImg = await loadImg(result);
      ctx.drawImage(qrCodeImg, 5, 122, 25, 25);

      if (code) {
        // 绘制邀请码
        ctx.fillStyle = "#FFF";
        ctx.font = "7px roboto-medium,Helvetica,Arial,sans-serif";
        ctx.fillText(`Kode Ajakan: ${code}`, 39, 140);
      }

      cavEl.current.toBlob(async (blob) => {
        if (blob) {
          const url = await putImage(blob);
          setRemoteUrl(url);
          // 上传完成缓存起来
          localStorage.setItem(`${growthPublishInviteCodeCacheKey}:${code}`, url);
          // const link = document.createElement("a");
          // link.href = window.URL.createObjectURL(blob);
          // link.download = `img.jpg`;
          // link.click();
          // window.URL.revokeObjectURL(link.href);
          // link.remove();
        }
      }, "image/jpeg");
    }

    cavEl.current && shareInfo?.shareContentUrl && draw();
  }, [cavEl, shareInfo]);

  return (
    <div className={styles.canvas_wrapper}>
      <canvas data-remote-url={remoteUrl} className={cls("off_screen_canvas", styles.canvas)} ref={cavEl} />
    </div>
  );
};

export default memo(shareCard);
